import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { costApi } from '../../api';
import { PageHeader, message, Modal } from '../../components/common';
import * as XLSX from 'xlsx';
import './CostSettlement.css';

const CostSettlement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settlementData, setSettlementData] = useState(null);
  const [allAdmins, setAllAdmins] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [showParticipantModal, setShowParticipantModal] = useState(false);

  useEffect(() => {
    fetchSettlementPreview();
  }, []);

  const fetchSettlementPreview = async (participantIds = null) => {
    setLoading(true);
    try {
      const res = await costApi.previewSettlement(participantIds ? { participant_ids: participantIds } : {});
      setSettlementData(res.data);
      setAllAdmins(res.data.all_admins || []);
      setSelectedParticipants(res.data.participant_ids || []);
    } catch (error) {
      message.error(error.message || '获取结算预览失败');
      setTimeout(() => navigate('/admin/cost-management'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleParticipantChange = (adminId) => {
    setSelectedParticipants(prev => {
      if (prev.includes(adminId)) {
        return prev.filter(id => id !== adminId);
      } else {
        return [...prev, adminId];
      }
    });
  };

  const handleApplyParticipants = () => {
    if (selectedParticipants.length === 0) {
      message.error('至少需要选择一个参与计算的人员');
      return;
    }
    setShowParticipantModal(false);
    fetchSettlementPreview(selectedParticipants);
  };

  const handleExportExcel = () => {
    if (!settlementData) return;

    const wb = XLSX.utils.book_new();

    // Sheet 1: 汇总信息
    const summaryData = [
      ['项目', '金额'],
      ['总收入', settlementData.summary.total_income.toFixed(2)],
      ['总支出', settlementData.summary.total_expense.toFixed(2)],
      ['净额', settlementData.summary.net_amount.toFixed(2)]
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
    ws1['!cols'] = [{ wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, ws1, '汇总信息');

    // Sheet 2: 人员结算明细
    const userSummaryData = [
      ['姓名', '实际支出', '应承担支出', '实际收入', '应得收入', '本次结算']
    ];
    settlementData.user_summary.filter(u => u.is_participant).forEach(user => {
      const settlementText = user.settlement_amount > 0 ? '应收' : user.settlement_amount < 0 ? '应付' : '无需结算';
      userSummaryData.push([
        user.user_name,
        user.actual_expense.toFixed(2),
        user.allocated_expense.toFixed(2),
        user.actual_income.toFixed(2),
        user.allocated_income.toFixed(2),
        `${user.settlement_amount.toFixed(2)} (${settlementText})`
      ]);
    });
    const ws2 = XLSX.utils.aoa_to_sheet(userSummaryData);
    ws2['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, ws2, '人员结算明细');

    // Sheet 3: 记录明细
    const recordsData = [
      ['ID', '类型', '渠道', '负责人', '账面金额', '实际金额', '规则', '录入时间']
    ];
    settlementData.records.forEach(record => {
      recordsData.push([
        record.id,
        record.cost_type_display,
        record.channel,
        record.manager,
        record.book_amount.toFixed(2),
        record.actual_amount.toFixed(2),
        record.rule,
        record.created_at
      ]);
    });
    const ws3 = XLSX.utils.aoa_to_sheet(recordsData);
    ws3['!cols'] = [
      { wch: 8 },
      { wch: 10 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 15 },
      { wch: 20 }
    ];
    XLSX.utils.book_append_sheet(wb, ws3, '记录明细');

    // 下载文件
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `成本结算报告_${timestamp}.xlsx`;
    XLSX.writeFile(wb, filename);

    message.success('Excel导出成功');
  };

  const handleCopyToClipboard = () => {
    if (!settlementData) return;

    let text = '=== 成本结算报告 ===\n\n';
    text += '【汇总信息】\n';
    text += `总收入：¥${settlementData.summary.total_income.toFixed(2)}\n`;
    text += `总支出：¥${settlementData.summary.total_expense.toFixed(2)}\n`;
    text += `净额：¥${settlementData.summary.net_amount.toFixed(2)}\n\n`;

    text += '【人员结算明细】\n';
    settlementData.user_summary.filter(u => u.is_participant).forEach(user => {
      text += `\n${user.user_name}：\n`;
      text += `  实际支出：¥${user.actual_expense.toFixed(2)}\n`;
      text += `  应承担支出：¥${user.allocated_expense.toFixed(2)}\n`;
      text += `  实际收入：¥${user.actual_income.toFixed(2)}\n`;
      text += `  应得收入：¥${user.allocated_income.toFixed(2)}\n`;
      const settlementText = user.settlement_amount > 0 ? '应收' : user.settlement_amount < 0 ? '应付' : '无需结算';
      text += `  本次结算：¥${user.settlement_amount.toFixed(2)} (${settlementText})\n`;
    });

    text += '\n【转账方案】\n';
    if (settlementData.transfer_plan && settlementData.transfer_plan.length > 0) {
      settlementData.transfer_plan.forEach((transfer, idx) => {
        text += `${idx + 1}. ${transfer.from_user_name} 转给 ${transfer.to_user_name}：¥${transfer.amount.toFixed(2)}\n`;
      });
    } else {
      text += '无需转账，所有人收支平衡。\n';
    }

    text += '\n【记录明细】\n';
    settlementData.records.forEach(record => {
      text += `#${record.id} ${record.cost_type_display} ${record.channel} 负责人:${record.manager} 实际金额:¥${record.actual_amount.toFixed(2)} 规则:${record.rule}\n`;
    });

    navigator.clipboard.writeText(text).then(() => {
      message.success('已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败');
    });
  };

  const handleConfirmSettlement = async () => {
    if (!window.confirm('确认要进行结算吗？结算后将无法撤销！')) return;

    try {
      setLoading(true);
      const res = await costApi.confirmSettlement({ participant_ids: selectedParticipants });
      message.success('结算成功！');
      setTimeout(() => navigate('/admin/cost-management'), 2000);
    } catch (error) {
      message.error(error.message || '结算失败');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/cost-management');
  };

  if (loading) {
    return (
      <div className="cost-settlement">
        <PageHeader title="成本结算" />
        <div className="loading">加载中...</div>
      </div>
    );
  }

  if (!settlementData) {
    return (
      <div className="cost-settlement">
        <PageHeader title="成本结算" />
        <div className="no-data">没有待结算的记录</div>
      </div>
    );
  }

  const participantCount = selectedParticipants.length;
  const participantNames = allAdmins.filter(a => selectedParticipants.includes(a.id)).map(a => a.real_name || a.username).join('、');

  return (
    <div className="cost-settlement">
      <PageHeader
        title="成本结算"
        extra={
          <div className="header-actions">
            <button onClick={handleExportExcel} className="btn-primary">导出Excel</button>
            <button onClick={handleCopyToClipboard} className="btn-secondary">复制到剪贴板</button>
            <button onClick={handleConfirmSettlement} className="btn-success">确认结算</button>
            <button onClick={handleCancel} className="btn-cancel">取消</button>
          </div>
        }
      />

      {/* 参与人员信息 */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '4px', border: '1px solid #2196f3' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <strong style={{ color: '#1976d2' }}>参与结算人员（{participantCount}人）：</strong>
            <span style={{ marginLeft: '10px' }}>{participantNames}</span>
          </div>
          <button onClick={() => setShowParticipantModal(true)} className="btn-secondary" style={{ fontSize: '14px' }}>
            编辑参与人员
          </button>
        </div>
      </div>

      {/* 汇总信息 */}
      <div className="summary-section">
        <h3>汇总信息</h3>
        <div className="summary-cards">
          <div className="summary-card income">
            <div className="label">总收入</div>
            <div className="value">¥{settlementData.summary.total_income.toFixed(2)}</div>
          </div>
          <div className="summary-card expense">
            <div className="label">总支出</div>
            <div className="value">¥{settlementData.summary.total_expense.toFixed(2)}</div>
          </div>
          <div className="summary-card net">
            <div className="label">净额</div>
            <div className="value">¥{settlementData.summary.net_amount.toFixed(2)}</div>
          </div>
          <div className="summary-card count">
            <div className="label">记录数</div>
            <div className="value">{settlementData.summary.record_count}</div>
          </div>
        </div>
      </div>

      {/* 人员结算明细 */}
      <div className="user-summary-section">
        <h3>人员结算明细</h3>
        <div className="note" style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <strong>说明：</strong>
          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
            <li>实际支出/收入：作为负责人实际支出或收到的金额</li>
            <li>应承担支出/应得收入：根据成本规则计算出的应该承担或得到的金额</li>
            <li>本次结算：正数表示应该收到钱，负数表示应该支付钱</li>
          </ul>
        </div>
        <table className="user-summary-table">
          <thead>
            <tr>
              <th>姓名</th>
              <th>实际支出</th>
              <th>应承担支出</th>
              <th>实际收入</th>
              <th>应得收入</th>
              <th>本次结算</th>
            </tr>
          </thead>
          <tbody>
            {settlementData.user_summary.filter(u => u.is_participant).map(user => (
              <tr key={user.user_id}>
                <td><strong>{user.user_name}</strong></td>
                <td className="expense">¥{user.actual_expense.toFixed(2)}</td>
                <td className="expense">¥{user.allocated_expense.toFixed(2)}</td>
                <td className="income">¥{user.actual_income.toFixed(2)}</td>
                <td className="income">¥{user.allocated_income.toFixed(2)}</td>
                <td className={user.settlement_amount > 0 ? 'positive' : user.settlement_amount < 0 ? 'negative' : ''}>
                  <strong>¥{user.settlement_amount.toFixed(2)}</strong>
                  <span style={{ fontSize: '12px', marginLeft: '5px' }}>
                    ({user.settlement_amount > 0 ? '应收' : user.settlement_amount < 0 ? '应付' : '无需结算'})
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 转账方案 */}
      <div className="transfer-plan-section">
        <h3>转账方案</h3>
        <div className="note" style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '4px', border: '1px solid #4caf50' }}>
          <strong style={{ color: '#2e7d32' }}>💰 请按以下方案进行转账：</strong>
        </div>
        {settlementData.transfer_plan && settlementData.transfer_plan.length > 0 ? (
          <div className="transfer-list">
            {settlementData.transfer_plan.map((transfer, idx) => (
              <div key={idx} className="transfer-item" style={{
                padding: '15px',
                marginBottom: '10px',
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
                  <span style={{
                    backgroundColor: '#2196f3',
                    color: 'white',
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}>
                    {idx + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '16px', color: '#f44336' }}>{transfer.from_user_name}</strong>
                    <span style={{ margin: '0 10px', color: '#666' }}>→</span>
                    <strong style={{ fontSize: '16px', color: '#4caf50' }}>{transfer.to_user_name}</strong>
                  </div>
                </div>
                <div style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#ff9800',
                  padding: '5px 15px',
                  backgroundColor: '#fff3e0',
                  borderRadius: '4px'
                }}>
                  ¥{transfer.amount.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            color: '#666'
          }}>
            ✅ 无需转账，所有人收支平衡。
          </div>
        )}
      </div>

      {/* 记录明细 */}
      <div className="records-section">
        <h3>记录明细</h3>
        <table className="records-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>类型</th>
              <th>渠道</th>
              <th>负责人</th>
              <th>账面金额</th>
              <th>实际金额</th>
              <th>规则</th>
              <th>录入时间</th>
            </tr>
          </thead>
          <tbody>
            {settlementData.records.map(record => (
              <tr key={record.id}>
                <td>{record.id}</td>
                <td>
                  <span className={`type-badge ${record.cost_type}`}>
                    {record.cost_type_display}
                  </span>
                </td>
                <td>{record.channel}</td>
                <td>{record.manager}</td>
                <td>¥{record.book_amount.toFixed(2)}</td>
                <td>¥{record.actual_amount.toFixed(2)}</td>
                <td>{record.rule}</td>
                <td>{record.created_at}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 参与人员选择模态框 */}
      <Modal
        visible={showParticipantModal}
        title="选择参与结算的人员"
        onClose={() => setShowParticipantModal(false)}
        onConfirm={handleApplyParticipants}
      >
        <div style={{ padding: '10px 0' }}>
          <p style={{ marginBottom: '15px', color: '#666' }}>
            请选择参与本次结算计算的人员（至少选择一人）：
          </p>
          {allAdmins.map(admin => (
            <div key={admin.id} style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedParticipants.includes(admin.id)}
                  onChange={() => handleParticipantChange(admin.id)}
                  style={{ marginRight: '10px' }}
                />
                <span>{admin.real_name || admin.username}</span>
              </label>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default CostSettlement;
